import React from 'react';
import { motion } from 'framer-motion';
import { Check, X, Target, Calendar, Battery, TrendingUp, BookOpen } from 'lucide-react';
export function WhatYouGet() {
  const contents = [{
    icon: Zap,
    text: 'The 7-minute "On Switch" Primer for dead bat days'
  }, {
    icon: Target,
    text: 'Med ball work that transfers (without turning into cardio)'
  }, {
    icon: TrendingUp,
    text: 'Lower-body strength + jump pairings that build snap'
  }, {
    icon: Battery,
    text: 'Swing-speed sessions you can do without a cage'
  }, {
    icon: Calendar,
    text: 'A 30-day plan built for real college schedules'
  }, {
    icon: Activity,
    text: 'In-season maintenance so you keep pop when it matters most'
  }, {
    icon: BookOpen,
    text: 'Tracking rules (simple, not obsessive)'
  }];
  // Helper for icons since we can't pass components directly in the array above easily without JSX
  function Zap(props: any) {
    return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-139">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" data-id="element-140"></polygon>
      </svg>;
  }
  function Activity(props: any) {
    return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-141">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" data-id="element-142"></polyline>
      </svg>;
  }
  return <section className="py-24 bg-[#0a0f1a] text-white border-t border-white/5" data-id="element-143">
      <div className="container mx-auto px-4" data-id="element-144">
        <div className="grid lg:grid-cols-12 gap-12" data-id="element-145">
          {/* Left Column: What's Inside */}
          <div className="lg:col-span-7" data-id="element-146">
            <h2 className="text-3xl font-bold mb-8" data-id="element-147">
              What you'll get inside the PDF
            </h2>
            <div className="bg-[#0f1623] rounded-2xl p-8 border border-white/5 shadow-xl" data-id="element-148">
              <ul className="space-y-6" data-id="element-149">
                {contents.map((item, i) => <motion.li key={i} initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.05
              }} className="flex items-start gap-4" data-id="element-150">
                    <div className="mt-1 p-1.5 rounded-full bg-blue-500/10 text-blue-400" data-id="element-151">
                      <item.icon size={18} data-id="element-152" />
                    </div>
                    <span className="text-lg text-gray-200" data-id="element-153">{item.text}</span>
                  </motion.li>)}
              </ul>
            </div>
          </div>

          {/* Right Column: Who it's for */}
          <div className="lg:col-span-5 space-y-8" data-id="element-154">
            {/* For */}
            <div className="bg-green-500/5 rounded-xl p-6 border border-green-500/10" data-id="element-155">
              <h3 className="text-xl font-bold text-green-400 mb-4 flex items-center gap-2" data-id="element-156">
                <Check size={20} data-id="element-157" /> Who it's for
              </h3>
              <ul className="space-y-3" data-id="element-158">
                <li className="flex items-start gap-3 text-gray-300" data-id="element-159">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2.5 shrink-0" data-id="element-160" />
                  College hitters who already train but want power that shows up
                  more often
                </li>
                <li className="flex items-start gap-3 text-gray-300" data-id="element-161">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2.5 shrink-0" data-id="element-162" />
                  Players who hate fluff and want a plan that feels professional
                </li>
              </ul>
            </div>

            {/* Not For */}
            <div className="bg-red-500/5 rounded-xl p-6 border border-red-500/10" data-id="element-163">
              <h3 className="text-xl font-bold text-red-400 mb-4 flex items-center gap-2" data-id="element-164">
                <X size={20} data-id="element-165" /> Who it's NOT for
              </h3>
              <ul className="space-y-3" data-id="element-166">
                <li className="flex items-start gap-3 text-gray-400" data-id="element-167">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500/50 mt-2.5 shrink-0" data-id="element-168" />
                  Anyone looking for a "two days to hit nukes" trick
                </li>
                <li className="flex items-start gap-3 text-gray-400" data-id="element-169">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500/50 mt-2.5 shrink-0" data-id="element-170" />
                  Players currently rehabbing acute injuries without clearance
                </li>
                <li className="flex items-start gap-3 text-gray-400" data-id="element-171">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500/50 mt-2.5 shrink-0" data-id="element-172" />
                  Total beginners with no training supervision
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Transformation */}
        <div className="mt-20 text-center max-w-3xl mx-auto" data-id="element-173">
          <h3 className="text-2xl font-bold mb-8" data-id="element-174">What changes in 30 days</h3>
          <div className="grid sm:grid-cols-2 gap-4 text-left" data-id="element-175">
            {['A bat that feels lighter more often', 'Cleaner, faster intent', 'Less panic when the swing feels off', 'A routine that brings you back online'].map((change, i) => <div key={i} className="flex items-center gap-3 p-4 bg-white/5 rounded-lg border border-white/5" data-id="element-176">
                <div className="w-2 h-2 bg-blue-500 rounded-full shadow-[0_0_10px_#3b82f6]" data-id="element-177" />
                <span className="font-medium text-white" data-id="element-178">{change}</span>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}